import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import {
  registerServiceWorker,
  subscribeToPush,
  getPushSupportStatus,
  setVapidPublicKey
} from '../utils/pushService';

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return null;
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  preferences: null,
  refresh: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  openNotificationPanel: () => {},
  ensurePushSubscription: async () => ({ success: false }),
  pushSupport: { supported: false, reason: 'unknown' },
  showReminder: false,
  setShowReminder: () => {},
  dismissReminder: () => {},
  dismissReminderForToday: () => {}
});

export const NotificationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [pushSupport, setPushSupport] = useState({ supported: false, reason: 'unknown' });
  const [vapidReady, setVapidReady] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const initialLoadRef = useRef(true);
  const lastSeenIdRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pollingRef = useRef(null);
  const serviceWorkerReadyRef = useRef(false);
  const reminderCheckIntervalRef = useRef(null);
  const metaLoadedRef = useRef(false);
  const notificationFetchInProgressRef = useRef(false);

  // localStorage'dan son görülen bildirim ID'sini yükle
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('jansoft-last-seen-notification-id');
        if (stored) {
          lastSeenIdRef.current = parseInt(stored, 10);
        }
      }
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const calculateUnread = useCallback((items = []) => {
    return items.filter((item) => !item.isRead).length;
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    const includeMeta = options?.includeMeta === true;
    const silent = options?.silent === true;
    if (!user || authLoading) return;
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    
    if (notificationFetchInProgressRef.current) {
      return;
    }

    notificationFetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      
      let notificationRes = { data: { data: { items: [] } } };
      const shouldLoadMeta = includeMeta || !metaLoadedRef.current;
      let prefRes = null;
      let vapidRes = null;
      
      try {
        notificationRes = await api.get('/api/notifications?limit=20');
      } catch (err) {
        if (err?.response?.status !== 401) {
          // 401 dışındaki hatalar sessizce atlanıyor
        }
      }
      
      if (shouldLoadMeta) {
        try {
          prefRes = await api.get('/api/notifications/preferences');
        } catch (err) {
          if (err?.response?.status !== 401) {
            // 401 dışındaki hatalar sessizce atlanıyor
          }
        }

        try {
          vapidRes = await api.get('/api/notifications/vapid-key');
        } catch (err) {
          // VAPID key hatası önemli değil
        }
      }
      
      const items = notificationRes.data?.data?.items || [];
      setNotifications(items);
      setUnreadCount(calculateUnread(items));
      if (shouldLoadMeta) {
        setPreferences(prefRes?.data?.data || null);
        const key = vapidRes?.data?.data?.publicKey || vapidRes?.data?.publicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';
        setVapidPublicKey(key);
        const supportStatus = getPushSupportStatus();
        setPushSupport(supportStatus);
        setVapidReady(!!key);
        metaLoadedRef.current = true;
      }
      handleIncomingNotifications(items);
    } catch (error) {
      // Sessizce atlanıyor
    } finally {
      notificationFetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user, authLoading, calculateUnread]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await api.post('/api/notifications/mark-read', { notificationId });
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        toast.error('Bildirim güncellenemedi');
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      toast.error('Bildirimler güncellenemedi');
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      setNotifications((prev) => {
        const deleted = prev.find((item) => item.id === notificationId);
        if (deleted && !deleted.isRead) {
          setUnreadCount((count) => Math.max(count - 1, 0));
        }
        return prev.filter((item) => item.id !== notificationId);
      });
    } catch (error) {
      toast.error('Bildirim silinemedi');
    }
  }, []);

  const openNotificationPanel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openNotificationPanel'));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    const unlockAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContextClass();
      }
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      audioUnlockedRef.current = true;
    };

    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const playSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!audioUnlockedRef.current) return;
      if (!audioCtxRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const showToastForNotification = useCallback((notification) => {
    if (!notification || !notification.title) return;
    try {
      playSound();
    } catch (error) {
      // Sessizce atlanıyor
    }
    try {
      toast.custom(
        (t) => {
          if (!t || !t.id) return null;
          const handleClose = (e) => {
            try {
              e?.stopPropagation();
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          const handleClick = () => {
            try {
              toast.dismiss(t.id);
              // Müşteri siparişi bildirimleri için yönlendirme
              if (notification.type === 'customer_order' && notification.metadata?.redirectUrl) {
                window.location.href = notification.metadata.redirectUrl;
              } else {
                openNotificationPanel();
              }
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          return (
            <div className="pointer-events-auto max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 relative">
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
                aria-label="Kapat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                onClick={handleClick}
                className="cursor-pointer pr-6"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">Yeni bildirim</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{notification.title || ''}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">
                  {notification.body || ''}
                </p>
              </div>
            </div>
          );
        },
        { duration: 5000 }
      );
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, [playSound, openNotificationPanel]);

  const handleIncomingNotifications = useCallback(
    (items) => {
      if (!items.length) return;
      const newestId = Math.max(...items.map((item) => item.id));
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('jansoft-last-seen-notification-id', newestId.toString());
        }
        return;
      }
      const lastSeen = lastSeenIdRef.current || 0;
      const newOnes = items.filter((item) => item.id > lastSeen);
      if (newOnes.length) {
        lastSeenIdRef.current = newestId;
        if (typeof window !== 'undefined') {
          localStorage.setItem('jansoft-last-seen-notification-id', newestId.toString());
        }
        newOnes.reverse().forEach(showToastForNotification);
      }
    },
    [showToastForNotification]
  );

  const ensurePushSubscription = useCallback(async () => {
    let loadingToastId = null;
    try {
      try {
        loadingToastId = toast.loading('Push bildirimleri etkinleştiriliyor...', { duration: 0 });
      } catch (toastInitError) {
        // Toast başlatılamazsa devam et
      }
      
      let supportStatus;
      try {
        supportStatus = typeof window !== 'undefined' ? getPushSupportStatus() : { supported: false };
        try {
          setPushSupport(supportStatus);
        } catch (stateError) {
          // State güncelleme hatası sessizce atlanıyor
        }
      } catch (statusError) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error('Push destek durumu kontrol edilemedi.');
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false, error: statusError };
      }
      
      if (!supportStatus || !supportStatus.supported) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          const reason = supportStatus?.reason || 'unknown';
          switch (reason) {
            case 'iosStandalone':
              toast.error('iOS\'ta bildirim için Safari\'den "Paylaş > Ana Ekrana Ekle" ile uygulamayı yükleyin.');
              break;
            case 'missingKey':
              toast.error('Push anahtarları sunucuda tanımlı değil. Yöneticiye haber verin.');
              break;
            case 'serviceWorker':
              toast.error('Tarayıcınız service worker desteklemiyor. Chrome veya Edge kullanmayı deneyin.');
              break;
            case 'notification':
              toast.error('Tarayıcınız bildirim API\'sini desteklemiyor.');
              break;
            default:
              toast.error('Push bildirimleri bu cihazda etkinleştirilemiyor.');
          }
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false };
      }
      
      if (!vapidReady) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error('Push anahtarları hazır değil; sayfayı yenileyip tekrar deneyin.');
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false };
      }
      
      if (typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission === 'denied') {
          try {
            if (loadingToastId) toast.dismiss(loadingToastId);
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const message = isMobile 
              ? 'Bildirim izinleri engellenmiş. Telefon ayarlarından (Ayarlar > Uygulamalar > [Tarayıcı] > Bildirimler) izin verin.'
              : 'Bildirim izinleri engellenmiş. Tarayıcı ayarlarından (🔒 kilit simgesi > Site ayarları > Bildirimler) izin verin.';
            toast.error(message);
          } catch (toastError) {
            // Toast hatası sessizce atlanıyor
          }
          return { success: false };
        }
      }
      
      let registration;
      try {
        if (!serviceWorkerReadyRef.current) {
          registration = await registerServiceWorker();
          if (!registration) {
            try {
              if (loadingToastId) toast.dismiss(loadingToastId);
              toast.error('Service Worker kaydedilemedi. HTTPS üzerinden eriştiğinizden emin olun.');
            } catch (toastError) {
              // Toast hatası sessizce atlanıyor
            }
            return { success: false };
          }
          serviceWorkerReadyRef.current = true;
        } else {
          // Service worker zaten kayıtlıysa mevcut registration'ı al
          if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            registration = await navigator.serviceWorker.ready;
          }
        }
      } catch (swError) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error(`Service Worker hatası: ${swError.message || 'Bilinmeyen hata'}`);
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false, error: swError };
      }
      
      if (!registration) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error('Service Worker registration bulunamadı.');
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false };
      }
      
      if (!registration.pushManager) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error('Push Manager bu tarayıcıda desteklenmiyor.');
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false };
      }
      
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          loadingToastId = toast.loading('Bildirim izni isteniyor...', { duration: 2000 });
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
      }
      
      let subscription;
      try {
        subscription = await subscribeToPush(registration);
      } catch (subError) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          const errorMessage = subError?.message || 'Push aboneliği oluşturulamadı';
          toast.error(errorMessage);
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false, error: subError };
      }
      
      if (!subscription) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          if (Notification.permission === 'denied') {
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const message = isMobile 
              ? 'Bildirim izinleri engellenmiş. Telefon ayarlarından izin verin.'
              : 'Bildirim izinleri engellenmiş. Tarayıcı ayarlarından yeniden izin verin.';
            toast.error(message);
          } else {
            toast.error('Push aboneliği oluşturulamadı.');
          }
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false };
      }
      
      try {
        if (!subscription || !subscription.endpoint) {
          throw new Error('Subscription endpoint bulunamadı.');
        }
        
        const p256dhKey = subscription.getKey('p256dh');
        const authKey = subscription.getKey('auth');
        
        if (!p256dhKey || !authKey) {
          throw new Error('Subscription keys bulunamadı.');
        }
        
        const p256dhBase64 = arrayBufferToBase64(p256dhKey);
        const authBase64 = arrayBufferToBase64(authKey);
        
        if (!p256dhBase64 || !authBase64) {
          throw new Error('Subscription keys serialize edilemedi.');
        }
        
        const userAgent = navigator.userAgent;
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const browser = userAgent.includes('Edg') ? 'edge' :
                       userAgent.includes('Chrome') ? 'chrome' :
                       userAgent.includes('Firefox') ? 'firefox' :
                       userAgent.includes('Safari') ? 'safari' : 'unknown';
        
        const subscriptionData = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: p256dhBase64,
            auth: authBase64
          },
          metadata: {
            userAgent,
            browser,
            deviceType: isMobile ? 'mobile' : 'desktop',
            timestamp: new Date().toISOString()
          }
        };
        
        if (!subscriptionData.endpoint || !subscriptionData.keys?.p256dh || !subscriptionData.keys?.auth) {
          throw new Error('Subscription eksik property içeriyor.');
        }
        
        try {
          await api.post('/api/notifications/push-subscription', subscriptionData);
        } catch (apiError) {
          try {
            if (loadingToastId) toast.dismiss(loadingToastId);
            toast.error(`Push aboneliği kaydedilemedi: ${apiError?.response?.data?.message || apiError?.message || 'Bilinmeyen hata'}`);
          } catch (toastError) {
            // Toast hatası sessizce atlanıyor
          }
          return { success: false, error: apiError };
        }
        
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          const deviceTypeText = isMobile ? 'mobil cihazda' : 'web tarayıcıda';
          toast.success(`Push bildirimleri ${deviceTypeText} etkinleştirildi`);
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        
        fetchNotifications({ includeMeta: true });
        return { success: true };
      } catch (serializeError) {
        try {
          if (loadingToastId) toast.dismiss(loadingToastId);
          toast.error(`Subscription işlenirken hata: ${serializeError?.message || 'Bilinmeyen hata'}`);
        } catch (toastError) {
          // Toast hatası sessizce atlanıyor
        }
        return { success: false, error: serializeError };
      }
    } catch (error) {
      try {
        if (loadingToastId) toast.dismiss(loadingToastId);
        const errorMessage = error?.message || 'Push bildirimi etkinleştirilemedi';
        toast.error(errorMessage);
      } catch (toastError) {
        // Toast hatası sessizce atlanıyor
      }
      return { success: false, error };
    }
  }, [fetchNotifications, vapidReady]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      metaLoadedRef.current = false;
      initialLoadRef.current = true;
      lastSeenIdRef.current = null;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    
    const delay = initialLoadRef.current ? 200 : 100;
    const timeoutId = setTimeout(() => {
      const currentToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
      if (!currentToken) return;
      
        fetchNotifications({ includeMeta: true });
      initialLoadRef.current = false;
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        const checkToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
        if (checkToken && user) {
          fetchNotifications({ silent: true });
        }
      }, 30000);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [user, authLoading, fetchNotifications]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NEW_NOTIFICATION') {
        fetchNotifications({ silent: true });
      }
      if (event.data?.type === 'OPEN_URL' && event.data?.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => document.removeEventListener('visibilitychange', visibilityHandler);
  }, [fetchNotifications]);

  useEffect(() => {
    if (vapidReady) {
      const newStatus = getPushSupportStatus();
      setPushSupport(newStatus);
    }
  }, [vapidReady]);

  // Hatırlatma kontrolü fonksiyonu
  const checkShouldShowReminder = useCallback(() => {
    // Kullanıcı yoksa veya sayfa görünür değilse hatırlatma gösterme
    if (!user || document.visibilityState !== 'visible') {
      return false;
    }

    // Okunmamış bildirim yoksa hatırlatma gösterme
    if (unreadCount === 0) {
      return false;
    }

    // Bugün hatırlatma kapatıldı mı kontrol et
    try {
      const dismissedToday = localStorage.getItem('jansoft-reminder-dismissed-today');
      if (dismissedToday) {
        const dismissedDate = new Date(dismissedToday);
        const today = new Date();
        if (
          dismissedDate.getDate() === today.getDate() &&
          dismissedDate.getMonth() === today.getMonth() &&
          dismissedDate.getFullYear() === today.getFullYear()
        ) {
          return false;
        }
      }
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }

    // Son hatırlatmadan 5 dakika geçti mi kontrol et
    try {
      const lastReminder = localStorage.getItem('jansoft-last-notification-reminder');
      if (lastReminder) {
        const lastReminderTime = parseInt(lastReminder, 10);
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000; // 5 dakika
        if (now - lastReminderTime < fiveMinutes) {
          return false;
        }
      }
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }

    return true;
  }, [user, unreadCount]);

  // Hatırlatmayı göster
  const showUnreadReminder = useCallback(() => {
    if (checkShouldShowReminder()) {
      setShowReminder(true);
      try {
        localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
      } catch (error) {
        // localStorage hatası sessizce atlanıyor
      }
    }
  }, [checkShouldShowReminder]);

  // Hatırlatmayı kapat (5 dakika sonra tekrar göster)
  const dismissReminder = useCallback(() => {
    setShowReminder(false);
    try {
      localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }
  }, []);

  // Bugün hatırlatmayı kapat (bugün bir daha gösterme)
  const dismissReminderForToday = useCallback(() => {
    setShowReminder(false);
    try {
      localStorage.setItem('jansoft-reminder-dismissed-today', new Date().toISOString());
      localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }
  }, []);

  // Periyodik hatırlatma kontrolü
  useEffect(() => {
    if (!user || authLoading) {
      if (reminderCheckIntervalRef.current) {
        clearInterval(reminderCheckIntervalRef.current);
        reminderCheckIntervalRef.current = null;
      }
      return;
    }

    // İlk kontrol
    if (unreadCount > 0 && !showReminder) {
      const timeoutId = setTimeout(() => {
        showUnreadReminder();
      }, 30000); // 30 saniye sonra ilk kontrol

      // Periyodik kontrol (her 1 dakikada bir)
      reminderCheckIntervalRef.current = setInterval(() => {
        if (!showReminder && checkShouldShowReminder()) {
          showUnreadReminder();
        }
      }, 60000); // 1 dakika

      return () => {
        clearTimeout(timeoutId);
        if (reminderCheckIntervalRef.current) {
          clearInterval(reminderCheckIntervalRef.current);
          reminderCheckIntervalRef.current = null;
        }
      };
    }
  }, [user, authLoading, unreadCount, showReminder, checkShouldShowReminder, showUnreadReminder]);

  // Sayfa görünürlüğü değiştiğinde hatırlatma kontrolü
  useEffect(() => {
    const reminderVisibilityHandler = () => {
      if (document.visibilityState === 'visible' && unreadCount > 0 && !showReminder) {
        setTimeout(() => {
          showUnreadReminder();
        }, 5000); // 5 saniye sonra kontrol
      }
    };
    document.addEventListener('visibilitychange', reminderVisibilityHandler);
    return () => document.removeEventListener('visibilitychange', reminderVisibilityHandler);
  }, [unreadCount, showReminder, showUnreadReminder]);

  const value = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh: () => fetchNotifications({ includeMeta: true }),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    openNotificationPanel,
    ensurePushSubscription,
    pushSupport,
    showReminder,
    setShowReminder,
    dismissReminder,
    dismissReminderForToday
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext);


